import type {
  CampaGift,
  FeatureFlag,
  GiftAuditEntry,
  GiftCampaign,
  GiftDispute,
  GiftLimit,
  GiftRiskFlag,
  GiftTypeConfig,
  Merchant,
  Redemption,
  Settlement,
} from '../types/campagift'
import { hashCode } from '../utils/campagift'

/**
 * DEVELOPMENT DATA. Every figure below is seeded demonstration state, isolated
 * from production. Production CampaGift metrics begin from real transactions.
 */
export const DEMO_DATA_NOTICE =
  'Development data — not production financial state. Production begins from real transactions.'

export const giftCategories = [
  { id: 'books', name: 'Books and study materials' },
  { id: 'food', name: 'Food and refreshment' },
  { id: 'print', name: 'Printing and stationery' },
  { id: 'laundry', name: 'Laundry' },
  { id: 'events', name: 'Event tickets' },
  { id: 'library', name: 'Library resources' },
  { id: 'services', name: 'Student services' },
] as const

/* ------------------------------------------------------------------ *
 * GIFT TYPES — configuration. Adding a programme is an admin action.
 * ------------------------------------------------------------------ */

export const giftTypes: GiftTypeConfig[] = [
  {
    id: 'cgt-general',
    name: 'CampaGift General',
    description: 'Redeemable across every participating Campuzon merchant.',
    scope: 'general',
    currency: 'NGN',
    funding: 'user_purchase',
    minValueKobo: 100_000,
    maxValueKobo: 20_000_000,
    transferability: 'transferable',
    expiryDays: 365,
    partialRedemption: true,
    minRedemptionKobo: 20_000,
    maxRedemptionPerTransactionKobo: null,
    maxDailyRedemptionKobo: null,
    eligibleMerchantIds: 'all',
    eligibleCategoryIds: 'all',
    purchaseFeeBps: 150,
    merchantCommissionBps: 500,
    acceptanceRequired: false,
    terms: [
      'Value is redeemable only with participating Campuzon merchants.',
      'Remaining value expires 365 days after issue. Expiry does not erase the record.',
      'CampaGift is not cash and cannot be withdrawn.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-books',
    name: 'CampaGift Books',
    description: 'Restricted to books, study materials and library resources.',
    scope: 'category',
    currency: 'NGN',
    funding: 'user_purchase',
    minValueKobo: 100_000,
    maxValueKobo: 10_000_000,
    transferability: 'category_bound',
    expiryDays: 180,
    partialRedemption: true,
    minRedemptionKobo: 20_000,
    maxRedemptionPerTransactionKobo: 5_000_000,
    maxDailyRedemptionKobo: 5_000_000,
    eligibleMerchantIds: 'all',
    eligibleCategoryIds: ['books', 'library', 'print'],
    purchaseFeeBps: 100,
    merchantCommissionBps: 400,
    acceptanceRequired: false,
    terms: [
      'Eligible for books, study materials, printing and library resources only.',
      'Not eligible for food, electronics or airtime.',
      'Remaining value expires 180 days after issue.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-merchant',
    name: 'CampaGift Merchant',
    description: 'Bound to one named merchant chosen at purchase.',
    scope: 'merchant',
    currency: 'NGN',
    funding: 'user_purchase',
    minValueKobo: 50_000,
    maxValueKobo: 5_000_000,
    transferability: 'merchant_bound',
    expiryDays: 90,
    partialRedemption: true,
    minRedemptionKobo: 10_000,
    maxRedemptionPerTransactionKobo: null,
    maxDailyRedemptionKobo: null,
    eligibleMerchantIds: ['mch-campusbooks'],
    eligibleCategoryIds: 'all',
    purchaseFeeBps: 0,
    merchantCommissionBps: 300,
    acceptanceRequired: false,
    terms: [
      'Usable only with the merchant named on the instrument.',
      'Cannot be transferred to another merchant.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-event',
    name: 'CampaGift Event',
    description: 'Redeemable against eligible Campuzon event tickets.',
    scope: 'event',
    currency: 'NGN',
    funding: 'user_purchase',
    minValueKobo: 50_000,
    maxValueKobo: 5_000_000,
    transferability: 'transferable',
    expiryDays: 120,
    partialRedemption: true,
    minRedemptionKobo: 50_000,
    maxRedemptionPerTransactionKobo: null,
    maxDailyRedemptionKobo: null,
    eligibleMerchantIds: 'all',
    eligibleCategoryIds: ['events'],
    purchaseFeeBps: 100,
    merchantCommissionBps: 350,
    acceptanceRequired: true,
    terms: [
      'Eligible for participating event tickets only.',
      'The recipient must accept the gift before it becomes redeemable.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-library',
    name: 'CampaGift Library',
    description:
      'Redeemable against library resources. A successful redemption creates a Library entitlement — the entitlement is not the gift.',
    scope: 'library',
    currency: 'NGN',
    funding: 'user_purchase',
    minValueKobo: 50_000,
    maxValueKobo: 5_000_000,
    transferability: 'non_transferable',
    expiryDays: 365,
    partialRedemption: true,
    minRedemptionKobo: 20_000,
    maxRedemptionPerTransactionKobo: null,
    maxDailyRedemptionKobo: null,
    eligibleMerchantIds: ['mch-library'],
    eligibleCategoryIds: ['library', 'books'],
    purchaseFeeBps: 0,
    merchantCommissionBps: 250,
    acceptanceRequired: false,
    terms: [
      'Redemption creates a Library entitlement governed by the resource licence.',
      'Non-transferable once assigned.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-campaign-bts',
    name: 'Back to School CampaGift',
    description: 'Campuzon-funded campaign value for books and study materials.',
    scope: 'campaign',
    currency: 'NGN',
    funding: 'campaign_budget',
    minValueKobo: 500_000,
    maxValueKobo: 500_000,
    transferability: 'non_transferable',
    expiryDays: 45,
    partialRedemption: true,
    minRedemptionKobo: 20_000,
    maxRedemptionPerTransactionKobo: 250_000,
    maxDailyRedemptionKobo: 250_000,
    eligibleMerchantIds: ['mch-campusbooks', 'mch-library', 'mch-printhub'],
    eligibleCategoryIds: ['books', 'library', 'print'],
    purchaseFeeBps: 0,
    merchantCommissionBps: 200,
    acceptanceRequired: false,
    terms: [
      'Promotional value funded from a campaign budget. Not purchased value.',
      'Non-transferable and non-refundable.',
      'Expires 45 days after issue.',
    ],
    enabled: true,
  },
  {
    id: 'cgt-promo-merchant',
    name: 'Merchant promotional CampaGift',
    description: 'Issued by a merchant as promotional value under platform rules.',
    scope: 'promotional',
    currency: 'NGN',
    funding: 'merchant_promotion',
    minValueKobo: 50_000,
    maxValueKobo: 1_000_000,
    transferability: 'merchant_bound',
    expiryDays: 30,
    partialRedemption: false,
    minRedemptionKobo: 50_000,
    maxRedemptionPerTransactionKobo: null,
    maxDailyRedemptionKobo: null,
    eligibleMerchantIds: ['mch-jollofspot'],
    eligibleCategoryIds: 'all',
    purchaseFeeBps: 0,
    merchantCommissionBps: 0,
    acceptanceRequired: false,
    terms: [
      'Promotional value issued by the merchant, not purchased by a user.',
      'Must be redeemed in one transaction and cannot be transferred.',
      'Expires 30 days after issue and is not refundable.',
    ],
    enabled: true,
  },
]

/* ------------------------------------------------------------------ *
 * MERCHANTS
 * ------------------------------------------------------------------ */

export const merchants: Merchant[] = [
  {
    id: 'mch-campusbooks',
    name: 'Campus Books',
    category: 'Bookshop',
    categoryId: 'books',
    campusId: 'cmp-futa',
    campus: 'FUTA',
    campaGiftEnabled: true,
    eligibleTypeIds: [
      'cgt-general',
      'cgt-books',
      'cgt-merchant',
      'cgt-campaign-bts',
    ],
    commissionBps: 450,
    settlementSchedule: 'Weekly, Wednesdays',
    riskState: 'clear',
    participation: 'active',
    settlementAccountMasked: '••••4417',
    dailyRedemptionCapKobo: 50_000_000,
  },
  {
    id: 'mch-library',
    name: 'Campuzon Library',
    category: 'Library resources',
    categoryId: 'library',
    campusId: 'cmp-all',
    campus: 'All campuses',
    campaGiftEnabled: true,
    eligibleTypeIds: ['cgt-general', 'cgt-books', 'cgt-library', 'cgt-campaign-bts'],
    commissionBps: null,
    settlementSchedule: 'Monthly',
    riskState: 'clear',
    participation: 'active',
    settlementAccountMasked: 'Internal',
    dailyRedemptionCapKobo: null,
  },
  {
    id: 'mch-printhub',
    name: 'Akure Print Hub',
    category: 'Printing and stationery',
    categoryId: 'print',
    campusId: 'cmp-futa',
    campus: 'FUTA',
    campaGiftEnabled: true,
    eligibleTypeIds: ['cgt-general', 'cgt-books', 'cgt-campaign-bts'],
    commissionBps: 500,
    settlementSchedule: 'Weekly, Fridays',
    riskState: 'clear',
    participation: 'active',
    settlementAccountMasked: '••••8102',
    dailyRedemptionCapKobo: 20_000_000,
  },
  {
    id: 'mch-jollofspot',
    name: 'The Jollof Spot',
    category: 'Food vendor',
    categoryId: 'food',
    campusId: 'cmp-futa',
    campus: 'FUTA',
    campaGiftEnabled: true,
    eligibleTypeIds: ['cgt-general', 'cgt-promo-merchant'],
    commissionBps: 600,
    settlementSchedule: 'Weekly, Mondays',
    riskState: 'review',
    participation: 'active',
    settlementAccountMasked: '••••2290',
    dailyRedemptionCapKobo: 10_000_000,
  },
  {
    id: 'mch-freshfold',
    name: 'FreshFold Laundry',
    category: 'Laundry',
    categoryId: 'laundry',
    campusId: 'cmp-futa',
    campus: 'FUTA',
    campaGiftEnabled: false,
    eligibleTypeIds: [],
    commissionBps: null,
    settlementSchedule: 'Not configured',
    riskState: 'clear',
    participation: 'pending',
    settlementAccountMasked: 'Not provided',
    dailyRedemptionCapKobo: null,
  },
]

/** The merchant console is signed in as this merchant. */
export const activeMerchantId = 'mch-campusbooks'

/* ------------------------------------------------------------------ *
 * INSTRUMENTS. Codes are held hashed; these plaintexts exist only so
 * the demonstration can exercise a real code check.
 * ------------------------------------------------------------------ */

export const demoCodes: Record<string, string> = {
  'cg-1001': 'K7QM-3JHR-9TWX',
  'cg-1002': 'B4LP-8XND-2VKS',
  'cg-1003': 'R9TC-5MFJ-7HQZ',
  'cg-1004': 'P2WD-6KNL-4RSX',
  'cg-1005': 'M8HV-2QTB-5JLC',
}

function gift(input: Omit<CampaGift, 'codeHash' | 'codeLast4'>): CampaGift {
  const code = demoCodes[input.id] ?? 'ZZZZ-ZZZZ-ZZZZ'
  return { ...input, codeHash: hashCode(code), codeLast4: code.slice(-4) }
}

export const campaGifts: CampaGift[] = [
  gift({
    id: 'cg-1001',
    publicReference: 'CG-4H8K2M',
    instrumentId: 'CGI-000000481',
    typeId: 'cgt-general',
    issuer: { id: 'usr-saviour', kind: 'user', name: '@saviour', authorised: true },
    purchaserHandle: 'saviour',
    holderHandle: 'mary.o',
    faceValueKobo: 1_000_000,
    currency: 'NGN',
    issuedAt: '2026-08-02',
    expiresAt: '2027-08-02',
    status: 'available',
    message: 'Happy birthday! Get those textbooks.',
    campaignId: null,
    riskState: 'clear',
    acceptance: 'auto',
    transfers: [],
  }),
  gift({
    id: 'cg-1002',
    publicReference: 'CG-9PD4LX',
    instrumentId: 'CGI-000000512',
    typeId: 'cgt-books',
    issuer: { id: 'usr-ifeoma', kind: 'user', name: '@ifeoma.e', authorised: true },
    purchaserHandle: 'ifeoma.e',
    holderHandle: 'mary.o',
    faceValueKobo: 1_500_000,
    currency: 'NGN',
    issuedAt: '2026-07-18',
    expiresAt: '2027-01-14',
    status: 'partially_redeemed',
    message: 'Good luck with second semester.',
    campaignId: null,
    riskState: 'clear',
    acceptance: 'auto',
    transfers: [],
  }),
  gift({
    id: 'cg-1003',
    publicReference: 'CG-2VN7RQ',
    instrumentId: 'CGI-000000544',
    typeId: 'cgt-campaign-bts',
    issuer: { id: 'org-campuzon', kind: 'campuzon', name: 'Campuzon', authorised: true },
    purchaserHandle: 'campuzon',
    holderHandle: 'mary.o',
    faceValueKobo: 500_000,
    currency: 'NGN',
    issuedAt: '2026-08-05',
    expiresAt: '2026-09-19',
    status: 'available',
    message: 'Back to School — books and study materials.',
    campaignId: 'cmpn-bts-2026',
    riskState: 'clear',
    acceptance: 'auto',
    transfers: [],
  }),
  gift({
    id: 'cg-1004',
    publicReference: 'CG-6TB1WC',
    instrumentId: 'CGI-000000559',
    typeId: 'cgt-event',
    issuer: { id: 'usr-tunde', kind: 'user', name: '@tunde.a', authorised: true },
    purchaserHandle: 'tunde.a',
    holderHandle: 'mary.o',
    faceValueKobo: 700_000,
    currency: 'NGN',
    issuedAt: '2026-08-12',
    expiresAt: '2026-12-10',
    status: 'issued',
    message: 'For the engineering dinner.',
    campaignId: null,
    riskState: 'clear',
    acceptance: 'pending',
    transfers: [],
  }),
  gift({
    id: 'cg-1005',
    publicReference: 'CG-8JK5ZV',
    instrumentId: 'CGI-000000402',
    typeId: 'cgt-general',
    issuer: { id: 'usr-mary', kind: 'user', name: '@mary.o', authorised: true },
    purchaserHandle: 'mary.o',
    holderHandle: 'chidi.n',
    faceValueKobo: 600_000,
    currency: 'NGN',
    issuedAt: '2026-06-20',
    expiresAt: '2027-06-20',
    status: 'fully_redeemed',
    message: 'Thanks for the notes.',
    campaignId: null,
    riskState: 'clear',
    acceptance: 'auto',
    transfers: [],
  }),
]

/* ------------------------------------------------------------------ *
 * REDEMPTIONS. Reversals sit BESIDE originals; nothing is rewritten.
 * ------------------------------------------------------------------ */

export const redemptions: Redemption[] = [
  {
    id: 'rd-001',
    reference: 'CGR-2026-000311',
    giftId: 'cg-1002',
    merchantId: 'mch-campusbooks',
    amountKobo: 600_000,
    commissionKobo: 27_000,
    netReceivableKobo: 573_000,
    at: '2026-07-24T10:22:00Z',
    state: 'confirmed',
    actor: 'Campus Books · till 2',
    settlementId: 'stl-001',
    idempotencyKey: 'idem-7f22a1',
  },
  {
    id: 'rd-002',
    reference: 'CGR-2026-000348',
    giftId: 'cg-1005',
    merchantId: 'mch-printhub',
    amountKobo: 200_000,
    commissionKobo: 10_000,
    netReceivableKobo: 190_000,
    at: '2026-07-30T14:05:00Z',
    state: 'confirmed',
    actor: 'Akure Print Hub · counter',
    settlementId: 'stl-002',
    idempotencyKey: 'idem-91b0cc',
  },
  {
    id: 'rd-003',
    reference: 'CGR-2026-000352',
    giftId: 'cg-1005',
    merchantId: 'mch-campusbooks',
    amountKobo: 400_000,
    commissionKobo: 18_000,
    netReceivableKobo: 382_000,
    at: '2026-08-01T09:41:00Z',
    state: 'confirmed',
    actor: 'Campus Books · till 1',
    settlementId: 'stl-001',
    idempotencyKey: 'idem-4ad39e',
  },
  {
    id: 'rd-004',
    reference: 'CGR-2026-000355',
    giftId: 'cg-1005',
    merchantId: 'mch-campusbooks',
    amountKobo: 100_000,
    commissionKobo: 4_500,
    netReceivableKobo: 95_500,
    at: '2026-08-01T09:48:00Z',
    state: 'reversed',
    actor: 'Campus Books · till 1',
    settlementId: null,
    idempotencyKey: 'idem-4ad39f',
    reason: 'Duplicate scan at the till',
  },
  {
    id: 'rd-005',
    reference: 'CGR-2026-000356',
    giftId: 'cg-1005',
    merchantId: 'mch-campusbooks',
    amountKobo: -100_000,
    commissionKobo: -4_500,
    netReceivableKobo: -95_500,
    at: '2026-08-01T11:12:00Z',
    state: 'confirmed',
    actor: 'Finance · adaeze.k',
    settlementId: null,
    idempotencyKey: 'idem-rev-4ad39f',
    reversalOfReference: 'CGR-2026-000355',
    reason: 'Authorised reversal of duplicate redemption',
  },
]

/* ------------------------------------------------------------------ *
 * SETTLEMENT
 * ------------------------------------------------------------------ */

export const settlements: Settlement[] = [
  {
    id: 'stl-001',
    reference: 'CGS-2026-W30-CB',
    merchantId: 'mch-campusbooks',
    redemptionReferences: ['CGR-2026-000311', 'CGR-2026-000352'],
    grossKobo: 1_000_000,
    commissionKobo: 45_000,
    adjustmentsKobo: 0,
    netKobo: 955_000,
    state: 'settled',
    openedAt: '2026-07-27',
    settledAt: '2026-08-05',
    provider: 'Licensed payout provider',
    providerReference: 'PP-88213-A',
  },
  {
    id: 'stl-002',
    reference: 'CGS-2026-W31-PH',
    merchantId: 'mch-printhub',
    redemptionReferences: ['CGR-2026-000348'],
    grossKobo: 200_000,
    commissionKobo: 10_000,
    adjustmentsKobo: 0,
    netKobo: 190_000,
    state: 'eligible',
    openedAt: '2026-08-03',
    settledAt: null,
    provider: 'Licensed payout provider',
    providerReference: null,
  },
]

export const campaigns: GiftCampaign[] = [
  {
    id: 'cmpn-bts-2026',
    name: 'Back to School 2026',
    typeId: 'cgt-campaign-bts',
    budgetKobo: 250_000_000,
    issuedKobo: 41_500_000,
    startsAt: '2026-08-01',
    endsAt: '2026-09-15',
    giftValueKobo: 500_000,
    maxGifts: 500,
    issuedCount: 83,
    state: 'running',
    owner: 'Gift operations · adaeze.k',
  },
  {
    id: 'cmpn-food-promo',
    name: 'Jollof Spot spend-and-get',
    typeId: 'cgt-promo-merchant',
    budgetKobo: 20_000_000,
    issuedKobo: 3_200_000,
    startsAt: '2026-08-10',
    endsAt: '2026-09-10',
    giftValueKobo: 200_000,
    maxGifts: 100,
    issuedCount: 16,
    state: 'running',
    owner: 'Merchant · The Jollof Spot',
  },
]

export const disputes: GiftDispute[] = [
  {
    id: 'dsp-001',
    reference: 'CGD-2026-0042',
    giftId: 'cg-1005',
    raisedBy: '@chidi.n',
    raisedByRole: 'user',
    kind: 'Incorrect redemption amount',
    detail: 'Charged twice for the same purchase at Campus Books.',
    evidence: ['Till receipt photograph', 'Redemption CGR-2026-000355'],
    state: 'resolved',
    reviewer: 'Finance · adaeze.k',
    decision:
      'Duplicate confirmed. Redemption reversed by compensating entry CGR-2026-000356. Original record preserved.',
    at: '2026-08-01',
  },
  {
    id: 'dsp-002',
    reference: 'CGD-2026-0051',
    giftId: 'cg-1002',
    raisedBy: 'The Jollof Spot',
    raisedByRole: 'merchant',
    kind: 'Merchant eligibility',
    detail: 'Customer expected a Books gift to be accepted for food.',
    evidence: ['Failed redemption attempt log'],
    state: 'resolved',
    reviewer: 'Gift operations · tobi.a',
    decision:
      'Refusal correct. CampaGift Books is category-bound to books, library and print. No adjustment made.',
    at: '2026-08-08',
  },
]

export const riskFlags: GiftRiskFlag[] = [
  {
    id: 'rf-001',
    giftId: null,
    signal: 'Gift code guessing',
    detail:
      '14 failed redemption code attempts from one device in 4 minutes. Rate limit engaged; no gift matched.',
    severity: 'high',
    state: 'escalated',
    at: '2026-08-11',
  },
  {
    id: 'rf-002',
    giftId: 'cg-1002',
    signal: 'Redemption velocity',
    detail: 'Three redemptions within nine minutes across two merchants.',
    severity: 'low',
    state: 'cleared',
    at: '2026-07-24',
  },
  {
    id: 'rf-003',
    giftId: null,
    signal: 'Circular gifting',
    detail:
      'Two accounts gifted the same value back and forth four times in one day. Flagged for review — no action taken automatically.',
    severity: 'medium',
    state: 'open',
    at: '2026-08-13',
  },
]

export const limits: GiftLimit[] = [
  {
    id: 'lim-1',
    scope: 'User · daily purchase',
    label: 'Maximum CampaGift purchased per day',
    valueKobo: 20_000_000,
    count: null,
    note: 'Evaluated before checkout. Raised per risk tier.',
  },
  {
    id: 'lim-2',
    scope: 'User · daily gifting',
    label: 'Maximum gifts sent per day',
    valueKobo: null,
    count: 10,
    note: 'Rapid gifting beyond this is flagged, not blocked.',
  },
  {
    id: 'lim-3',
    scope: 'Instrument · single gift value',
    label: 'Maximum face value of one gift',
    valueKobo: 20_000_000,
    count: null,
    note: 'Also bounded by the gift type maximum.',
  },
  {
    id: 'lim-4',
    scope: 'Merchant · daily redemption',
    label: 'Maximum redeemed per merchant per day',
    valueKobo: 50_000_000,
    count: null,
    note: 'Configured per merchant. Campus Books: ₦500,000.',
  },
  {
    id: 'lim-5',
    scope: 'Campaign · budget',
    label: 'Campaign issuance cannot exceed its budget',
    valueKobo: 250_000_000,
    count: 500,
    note: 'Issuance stops when either the budget or gift count is reached.',
  },
]

export const featureFlags: FeatureFlag[] = [
  {
    key: 'campa_gift',
    label: 'CampaGift module',
    enabled: true,
    rollout: 'All campuses',
    note: 'Master switch for the instrument, catalogue and wallet.',
  },
  {
    key: 'campa_gift_purchase',
    label: 'Purchase with real money',
    enabled: false,
    rollout: 'Disabled',
    note: 'Requires payment provider onboarding and the compliance gate.',
  },
  {
    key: 'campa_gift_transfer',
    label: 'Holder-to-holder transfer',
    enabled: true,
    rollout: 'FUTA pilot',
    note: 'Only for gift types configured as transferable.',
  },
  {
    key: 'campa_gift_partial_redemption',
    label: 'Partial redemption',
    enabled: true,
    rollout: 'All merchants',
    note: 'Per gift type; some types require full redemption.',
  },
  {
    key: 'campa_gift_merchant_redemption',
    label: 'Merchant redemption desk',
    enabled: true,
    rollout: 'Verified merchants',
    note: 'Scan and code entry, server-validated.',
  },
  {
    key: 'campa_gift_promotions',
    label: 'Merchant promotional issuance',
    enabled: true,
    rollout: 'The Jollof Spot',
    note: 'Promotional value is accounted separately from purchased value.',
  },
  {
    key: 'campa_gift_settlement',
    label: 'Merchant settlement disbursement',
    enabled: false,
    rollout: 'Disabled',
    note: 'Receivables accrue; payment execution needs a licensed provider.',
  },
  {
    key: 'campa_gift_bulk_issuance',
    label: 'Corporate and bulk issuance',
    enabled: false,
    rollout: 'Disabled',
    note: 'Batch model and CSV import are architected, not switched on.',
  },
]

export const giftAudit: GiftAuditEntry[] = [
  {
    id: 'ga-1',
    at: '2026-08-01 11:12',
    actor: 'Finance · adaeze.k',
    action: 'Redemption reversed',
    entity: 'CGR-2026-000355',
    from: 'confirmed',
    to: 'reversed',
    reason: 'Duplicate scan confirmed against till receipt',
  },
  {
    id: 'ga-2',
    at: '2026-08-05 09:02',
    actor: 'Finance · adaeze.k',
    action: 'Settlement approved',
    entity: 'CGS-2026-W30-CB',
    from: 'eligible',
    to: 'settled',
    reason: 'Weekly settlement run, reconciled against provider report',
  },
  {
    id: 'ga-3',
    at: '2026-08-09 16:30',
    actor: 'Gift operations · tobi.a',
    action: 'Merchant commission changed',
    entity: 'mch-campusbooks',
    from: '5%',
    to: '4.5%',
    reason: 'Renegotiated bookshop rate, effective this settlement period',
  },
  {
    id: 'ga-4',
    at: '2026-08-11 08:14',
    actor: 'Risk · system',
    action: 'Risk flag escalated',
    entity: 'rf-001',
    from: 'open',
    to: 'escalated',
    reason: 'Code-guessing pattern exceeded threshold; queued for human review',
  },
]

/** The signed-in student for this build. */
export const currentHandle = 'mary.o'
